import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, pct, thaiDateTime } from '../lib/format'

/**
 * Query cache console (part 81).
 *
 * This page inspects the SQL-side result cache, not the HTTP cache. The API deliberately does not
 * return the cached payload: an operator needs to know which aggregate is stored, what it depends
 * on and how often it was served, not to read another feature's cached answer. The only write is
 * an invalidation by tag, which is what a data-changing feature calls after it commits.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type EntryClass = 'dashboard' | 'aggregate' | 'report' | 'reference'

interface QueryCacheEntry {
  cache_key: string
  entry_class: EntryClass
  query_sql: string
  tags: string[]
  payload_bytes: number
  computed_ms: number
  hits: number
  created_at: string
  expires_at: string
  last_hit_at?: string
}

interface QueryCachePolicyRow {
  entry_class: EntryClass
  ttl_seconds: number
}

interface QueryCacheEntries {
  entries: QueryCacheEntry[]
  policy: QueryCachePolicyRow[]
  note: string
  server_at: string
}

interface QueryCacheClassStats {
  entry_class: EntryClass
  lookups: number
  hits: number
  misses: number
  coalesced: number
  writes: number
  invalidations: number
  compute_micros: number
  hit_micros: number
  updated_at?: string
  hit_rate: number
  avg_compute_micros: number
  avg_hit_micros: number
}

interface QueryCacheTotals {
  lookups: number
  hits: number
  misses: number
  coalesced: number
  writes: number
  invalidations: number
  compute_micros: number
  hit_micros: number
  hit_rate: number
}

interface QueryCacheStats {
  classes: QueryCacheClassStats[]
  totals: QueryCacheTotals
  policy: QueryCachePolicyRow[]
  in_flight: number
  waiting: number
  note: string
}

interface PurgeResult {
  tag: string
  purged: number
  by_class: Record<string, number>
  purged_at: string
  recomputed_on_next_call: boolean
  note: string
}

const CLASS_LABEL: Record<EntryClass, string> = {
  dashboard: 'แดชบอร์ด',
  aggregate: 'ผลรวม',
  report: 'รายงาน',
  reference: 'ข้อมูลอ้างอิง',
}

const ENTRIES_KEY = ['query-cache-entries']
const STATS_KEY = ['query-cache-stats']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function ttlLabel(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600} ชั่วโมง`
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

function micros(us: number): string {
  if (us <= 0) return '0 µs'
  if (us >= 1000) return `${(us / 1000).toFixed(1)} ms`
  return `${Math.round(us)} µs`
}

function bytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${n} B`
}

export function QueryCache() {
  const qc = useQueryClient()
  const [entryClass, setEntryClass] = useState<'' | EntryClass>('')
  const [tagFilter, setTagFilter] = useState('')
  const [purgeTag, setPurgeTag] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const entries = useQuery({
    queryKey: [...ENTRIES_KEY, entryClass, tagFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' })
      if (entryClass) params.set('class', entryClass)
      if (tagFilter.trim()) params.set('tag', tagFilter.trim())
      return api<QueryCacheEntries>(`/v1/query-cache/entries?${params.toString()}`)
    },
    staleTime: 10_000,
  })

  const stats = useQuery({
    queryKey: STATS_KEY,
    queryFn: () => api<QueryCacheStats>('/v1/query-cache/stats'),
    // The counters move with every cached read; polling is cheaper than a socket for a screen an
    // operator opens occasionally.
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  const purge = useMutation({
    mutationFn: (tag: string) => apiWrite<PurgeResult>('/v1/query-cache/purge', 'POST', { tag }),
    onSuccess: (result) => {
      const classes = Object.entries(result.by_class)
        .map(([cls, count]) => `${CLASS_LABEL[cls as EntryClass] ?? cls} ${number(count)}`)
        .join(' · ')
      setNotice(
        `ล้าง tag ${result.tag} แล้ว ${number(result.purged)} รายการ` +
          (classes ? ` (${classes})` : '') +
          ' · คำขอถัดไปจะคำนวณใหม่',
      )
      setConfirming(false)
      void qc.invalidateQueries({ queryKey: ENTRIES_KEY })
      void qc.invalidateQueries({ queryKey: STATS_KEY })
    },
  })

  const rows = entries.data?.entries ?? []
  const totals = stats.data?.totals
  const policy = stats.data?.policy ?? entries.data?.policy ?? []

  const policyByClass = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of policy) map.set(row.entry_class, row.ttl_seconds)
    return map
  }, [policy])

  return (
    <>
      <PageHead
        title="แคชคำสั่งรวมผล"
        sub="แคชผลลัพธ์ฝั่ง SQL · คำสั่งเดิม พารามิเตอร์เดิม และองค์กรเดิมจะอ่านจากตารางแคชแทนการสแกนตารางฐานข้อมูลซ้ำ"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{number(totals?.lookups ?? 0)} การอ่าน</span>
        <span className="chip gold">{pct(totals?.hit_rate ?? 0)} hit rate</span>
        <span className="chip">{number(totals?.coalesced ?? 0)} คำขอที่รอผลเดียว</span>
        <span className="chip">{number(totals?.invalidations ?? 0)} การล้าง</span>
        <span className="chip">{number(stats.data?.in_flight ?? 0)} กำลังคำนวณ</span>
      </div>

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="การอ่านทั้งหมด"
          value={number(totals?.lookups ?? 0)}
          unit="ครั้ง"
          foot={`hit ${number(totals?.hits ?? 0)} · miss ${number(totals?.misses ?? 0)}`}
        />
        <Stat
          label="อัตราการใช้แคช"
          value={pct(totals?.hit_rate ?? 0)}
          foot={`ล้างไปแล้ว ${number(totals?.invalidations ?? 0)} รายการ`}
        />
        <Stat
          label="เวลาเฉลี่ยเมื่อ hit"
          value={micros(
            totals && totals.hits > 0 ? Math.round(totals.hit_micros / totals.hits) : 0,
          )}
          foot="อ่านจากตารางแคช ไม่สแกนตารางจริง"
        />
        <Stat
          label="เวลาเฉลี่ยเมื่อ miss"
          value={micros(
            totals && totals.writes > 0 ? Math.round(totals.compute_micros / totals.writes) : 0,
          )}
          foot={`คำนวณใหม่ ${number(totals?.writes ?? 0)} ครั้ง`}
        />
      </div>

      {notice ? (
        <div
          className="card card-pad"
          role="status"
          style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}
        >
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 190 }}>
            <label htmlFor="qc-class">ชั้นของรายการ</label>
            <select
              id="qc-class"
              className="select"
              value={entryClass}
              onChange={(event) => setEntryClass(event.target.value as '' | EntryClass)}
            >
              <option value="">ทุกชั้น</option>
              {(Object.keys(CLASS_LABEL) as EntryClass[]).map((cls) => (
                <option key={cls} value={cls}>
                  {CLASS_LABEL[cls]}
                  {policyByClass.has(cls) ? ` · ${ttlLabel(policyByClass.get(cls) ?? 0)}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 240, flex: 1 }}>
            <label htmlFor="qc-tag-filter">กรองตาม tag</label>
            <input
              id="qc-tag-filter"
              className="input"
              placeholder="เช่น audit.findings"
              value={tagFilter}
              onChange={(event) => setTagFilter(event.target.value)}
            />
          </div>
          <button
            className="btn btn-sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ENTRIES_KEY })
              void qc.invalidateQueries({ queryKey: STATS_KEY })
            }}
          >
            รีเฟรช
          </button>
        </div>

        <div className="table-wrap" style={{ marginTop: 16 }}>
          {entries.isLoading ? (
            <div style={{ padding: 16 }}><Skel h={220} /></div>
          ) : entries.isError ? (
            <div className="form-error" style={{ margin: 16 }}>
              {errorText(entries.error, 'โหลดรายการแคชไม่สำเร็จ')}
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีรายการแคชที่ตรงกับตัวกรอง"
                body="รายการจะปรากฏเมื่อมีคำขอที่ใช้แคชฝั่ง SQL ครั้งแรก · ชั้น dashboard อายุ 15 วินาที ชั้น aggregate 60 วินาที"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>ชั้น</th>
                  <th>คำสั่ง SQL</th>
                  <th style={{ width: 190 }}>tag</th>
                  <th style={{ width: 80, textAlign: 'right' }}>hit</th>
                  <th style={{ width: 90, textAlign: 'right' }}>ขนาด</th>
                  <th style={{ width: 100, textAlign: 'right' }}>คำนวณ</th>
                  <th style={{ width: 150 }}>หมดอายุ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.cache_key} style={{ cursor: 'default' }}>
                    <td>
                      <span className={`sev ${row.entry_class === 'dashboard' ? 'sev-low' : row.entry_class === 'aggregate' ? 'sev-medium' : 'sev-high'}`}>
                        <i className="sev-dot" />
                        {CLASS_LABEL[row.entry_class] ?? row.entry_class}
                      </span>
                    </td>
                    <td>
                      <div
                        className="mono"
                        title={row.query_sql}
                        style={{ fontSize: 11.5, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {row.query_sql}
                      </div>
                      <div className="dim mono" style={{ fontSize: 10.5, marginTop: 4 }}>
                        {row.cache_key.slice(0, 12)}… · สร้าง {thaiDateTime(row.created_at)}
                        {row.last_hit_at ? ` · hit ล่าสุด ${thaiDateTime(row.last_hit_at)}` : ''}
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                        {row.tags.length === 0 ? (
                          <span className="dim" style={{ fontSize: 11 }}>ไม่มี tag</span>
                        ) : (
                          row.tags.map((tag) => (
                            <span key={tag} className="chip" style={{ fontSize: 10.5 }}>{tag}</span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.hits)}</td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>{bytes(row.payload_bytes)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 11.5 }}>{row.computed_ms} ms</td>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 22 }}>
        <div className="card card-pad">
          <div className="card-label">ล้างแคชตาม tag</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ใช้เมื่อข้อมูลต้นทางเปลี่ยนแล้วต้องการให้ผลรวมถูกคำนวณใหม่ทันที ·
            รายการทั้งหมดของ tag นี้จะถูกลบในคำสั่งเดียว และผลที่กำลังคำนวณค้างอยู่จะไม่ถูกเขียนกลับ
          </p>
          <div className="field">
            <label htmlFor="qc-purge">tag ที่ต้องการล้าง</label>
            <input
              id="qc-purge"
              className="input"
              placeholder="audit.findings"
              value={purgeTag}
              onChange={(event) => {
                setPurgeTag(event.target.value)
                setConfirming(false)
              }}
            />
          </div>
          <div className="row" style={{ gap: 8 }}>
            {confirming ? (
              <>
                <button
                  className="btn btn-primary"
                  disabled={purge.isPending}
                  style={{ color: 'var(--color-crimson)' }}
                  onClick={() => purge.mutate(purgeTag.trim())}
                >
                  {purge.isPending ? 'กำลังล้าง…' : 'ยืนยันการล้าง'}
                </button>
                <button className="btn btn-ghost" onClick={() => setConfirming(false)}>ยกเลิก</button>
              </>
            ) : (
              <button
                className="btn"
                disabled={!purgeTag.trim() || purge.isPending}
                onClick={() => setConfirming(true)}
              >
                ตรวจสอบและล้าง
              </button>
            )}
          </div>
          {purge.isError ? (
            <div className="form-error">{errorText(purge.error, 'ล้างแคชไม่สำเร็จ')}</div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">อายุของแต่ละชั้น</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            อายุถูกกำหนดจากชั้นของรายการ ไม่ใช่จากผู้เรียก · ชั้นเดียวกันใช้ค่าคงที่จากการ deploy
            และทุกครั้งที่เปลี่ยนค่าจะมีผลกับรายการที่สร้างใหม่
          </p>
          <table className="table data" style={{ marginTop: 10 }}>
            <tbody>
              {policy.map((row) => (
                <tr key={row.entry_class} style={{ cursor: 'default' }}>
                  <td style={{ fontSize: 12 }}>{CLASS_LABEL[row.entry_class] ?? row.entry_class}</td>
                  <td className="mono dim" style={{ fontSize: 11.5 }}>{row.entry_class}</td>
                  <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{ttlLabel(row.ttl_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {stats.data ? (
        <div className="card card-pad" style={{ marginBottom: 22 }}>
          <div className="row" style={{ marginBottom: 12 }}>
            <span className="card-label">บัญชี hit และ miss แยกตามชั้น</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              ตัวเลขสะสมจากทุกอินสแตนซ์ของ API
            </span>
          </div>
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>ชั้น</th>
                  <th style={{ textAlign: 'right' }}>อ่าน</th>
                  <th style={{ textAlign: 'right' }}>hit</th>
                  <th style={{ textAlign: 'right' }}>miss</th>
                  <th style={{ textAlign: 'right' }}>รอผลเดียว</th>
                  <th style={{ textAlign: 'right' }}>เขียน</th>
                  <th style={{ textAlign: 'right' }}>ล้าง</th>
                  <th style={{ textAlign: 'right' }}>hit rate</th>
                  <th style={{ textAlign: 'right' }}>เฉลี่ย compute</th>
                  <th style={{ textAlign: 'right' }}>เฉลี่ย hit</th>
                </tr>
              </thead>
              <tbody>
                {stats.data.classes.map((row) => (
                  <tr key={row.entry_class} style={{ cursor: 'default' }}>
                    <td style={{ fontSize: 12 }}>{CLASS_LABEL[row.entry_class] ?? row.entry_class}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.lookups)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.hits)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.misses)}</td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>{number(row.coalesced)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.writes)}</td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>{number(row.invalidations)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{pct(row.hit_rate)}</td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>{micros(Math.round(row.avg_compute_micros))}</td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>{micros(Math.round(row.avg_hit_micros))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="card card-pad">
        <div className="card-label">วิธีที่แคชชั้นนี้ทำงาน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>คีย์คือ SHA-256 ของ tenant + คำสั่ง SQL ที่ปรับช่องว่างแล้ว + พารามิเตอร์แบบ canonical · เวลา คำขอ และผู้ใช้ไม่ถูกใส่ในคีย์</li>
          <li>ทุกการอ่านและการเขียนอยู่ใน transaction ที่ตั้งค่า app.tenant_id และตารางบังคับ RLS ทั้งสองตาราง</li>
          <li>คำขอที่มาพร้อมกันสำหรับคีย์เดียวกันจะคำนวณเพียงครั้งเดียว คำขอที่รอจะนับเป็น hit และนับแยกเป็น “รอผลเดียว”</li>
          <li>การล้างตาม tag จะลบทุกชั้นที่ประกาศ tag นั้น และกันผลที่คำนวณค้างอยู่ก่อนการล้างไม่ให้ถูกเขียนกลับ</li>
          <li>แคช HTTP (ETag) และแคชที่ edge เป็นอีกชั้นหนึ่งและไม่ถูกแก้ไขจากหน้านี้</li>
          <li>รายการที่หมดอายุแล้วจะไม่ถูกอ่านและไม่ถูกแสดง · แถวที่หมดอายุสามารถลบได้ด้วยคำสั่งล้างตามกำหนดของฐานข้อมูล</li>
        </ul>
      </div>
    </>
  )
}
